// =============================================================================
// components/feed/PostCard.tsx — compact feed/bookmarks post card.
//
// BARE-RN swaps: expo-image Image → react-native Image (resizeMode='cover');
// @expo/vector-icons Ionicons → local Icon shim. Behaviour unchanged.
// =============================================================================
import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
} from 'react-native';
import CachedImage from '../ui/CachedImage';
import { Ionicons, IoniconName } from '../ui/Icon';
import RichPostText from './RichPostText';
import { navigate as navRef } from '../../navigation/navigationRef';
import Avatar from '../ui/Avatar';
import Card from '../ui/Card';
import { colors, fonts, spacing, radius } from '../../constants/theme';

export interface Post {
  id: string;
  user: {
    name: string;
    handle: string;
    avatar?: string;
  };
  content: string;
  image?: string;
  time: string;
  likes: number;
  comments: number;
  reposts: number;
  liked?: boolean;
  bookmarked?: boolean;
}

interface PostCardProps {
  post: Post;
  onPress?: () => void;
  onLike?: () => void;
  onComment?: () => void;
  // Optional bookmark toggle. When omitted the bookmark button hides (ActionButton
  // renders nothing without a handler) — passed on the Bookmarks screen so a saved
  // post can be un-bookmarked (removes the row).
  onBookmark?: () => void;
  // Trust & Safety (Apple 1.2): tapping the header kebab opens the report/block
  // sheet. Optional so existing callers that don't pass it simply hide the kebab.
  onOptions?: () => void;
}

function formatCount(n?: number): string {
  const v = typeof n === 'number' && isFinite(n) ? n : 0;
  if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v > 0 ? String(v) : '';
}

function PostCard({
  post,
  onPress,
  onLike,
  onComment,
  onBookmark,
  onOptions,
}: PostCardProps) {
  const user = post?.user || { name: 'User', handle: 'user' };
  return (
    <TouchableOpacity
      activeOpacity={0.9}
      onPress={onPress}
      disabled={!onPress}
      accessibilityRole={onPress ? 'button' : undefined}
      accessibilityLabel={onPress ? `Open post by ${user.name || user.handle || 'user'}` : undefined}
    >
      <Card style={styles.card}>
        {/* Header */}
        <View style={styles.header}>
          <Avatar uri={user.avatar} name={user.name} size={40} />
          <View style={styles.meta}>
            <View style={styles.nameRow}>
              <Text style={styles.name} numberOfLines={1}>
                {user.name || user.handle || 'User'}
              </Text>
              <Text style={styles.time} numberOfLines={1}>{post?.time || ''}</Text>
            </View>
            <Text style={styles.handle} numberOfLines={1}>@{user.handle || 'user'}</Text>
          </View>
          {onOptions ? (
            <TouchableOpacity
              onPress={onOptions}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              accessibilityRole="button"
              accessibilityLabel="Post options — report or block"
            >
              <Ionicons name="ellipsis-horizontal" size={18} color={colors.muted} />
            </TouchableOpacity>
          ) : null}
        </View>

        {/* Content — $CASHTAGS are tappable → CoinDetail (crypto-native feed). */}
        <RichPostText
          content={post?.content || ''}
          style={styles.content}
          collapsedLines={6}
          onCashtag={(symbol) => navRef('CoinDetail', { symbol })}
        />

        {/* Image */}
        {post?.image ? (
          <CachedImage
            source={{ uri: post.image }}
            style={styles.image}
            resizeMode="cover"
          />
        ) : null}

        {/* Actions */}
        <View style={styles.actions}>
          <ActionButton
            icon={post?.liked ? 'heart' : 'heart-outline'}
            count={post?.likes}
            color={post?.liked ? colors.error : colors.muted}
            onPress={onLike}
            label={post?.liked ? 'Unlike' : 'Like'}
          />
          <ActionButton
            icon="chatbubble-outline"
            count={post?.comments}
            onPress={onComment}
            label="Comment"
          />
          <ActionButton icon="repeat-outline" count={post?.reposts} label="Repost" />
          <ActionButton
            icon={post?.bookmarked ? 'bookmark' : 'bookmark-outline'}
            color={post?.bookmarked ? colors.primary : colors.muted}
            onPress={onBookmark}
            label={post?.bookmarked ? 'Remove bookmark' : 'Bookmark'}
          />
          <ActionButton icon="share-outline" label="Share" />
        </View>
      </Card>
    </TouchableOpacity>
  );
}

// Memoized so a parent's unrelated state change doesn't re-render every feed row.
// Only re-renders when this post's identity/counts/flags or a handler reference
// changes — so callers should pass STABLE (useCallback'd) handlers for the memo
// to actually hold (see app/bookmarks.tsx).
export default React.memo(PostCard);

function ActionButton({
  icon,
  count,
  color,
  onPress,
  label,
}: {
  icon: IoniconName;
  count?: number;
  color?: string;
  onPress?: () => void;
  label?: string;
}) {
  // Don't render a dead (non-functional) action button — repost/share/bookmark
  // had no handler here and rendered as permanently-disabled grey buttons.
  if (!onPress) return null;
  return (
    <TouchableOpacity
      style={styles.actionBtn}
      onPress={onPress}
      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      accessibilityRole="button"
      accessibilityLabel={label}
    >
      <Ionicons name={icon} size={18} color={color || colors.muted} />
      {count !== undefined && count > 0 ? (
        <Text style={styles.actionCount}>{formatCount(count)}</Text>
      ) : null}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  card: {
    marginBottom: spacing.md,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  meta: {
    flex: 1,
    marginLeft: spacing.md,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  name: {
    fontSize: 15,
    color: colors.ink,
    flex: 1,
    ...fonts.semibold,
  },
  handle: {
    fontSize: 13,
    color: colors.muted,
    marginTop: 1,
    ...fonts.regular,
  },
  time: {
    fontSize: 12,
    color: colors.muted,
    marginLeft: spacing.sm,
    ...fonts.regular,
  },
  content: {
    fontSize: 15,
    color: colors.ink,
    lineHeight: 22,
    ...fonts.regular,
  },
  image: {
    width: '100%',
    height: 200,
    borderRadius: radius.md,
    marginTop: spacing.md,
    backgroundColor: colors.faint,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: 'rgba(255,255,255,0.7)',
  },
  actionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  actionCount: {
    fontSize: 12,
    color: colors.muted,
    ...fonts.medium,
  },
});
